import { css, Global } from '@emotion/react';
import { useMount } from 'ahooks';
import { ConfigProvider } from 'antd';
import useSystem, { ColorMode, SystemSetting } from 'hooks/business/system/useSystem';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { isQuickChat } from 'productLine';
import React, { useContext, useMemo, useCallback, useEffect, useState } from 'react';
import { isMlj } from 'services/chattt';
import createNamedContext from 'utils/createNamedContext';

export type Themes = SystemSetting['theme'];
const defaultTheme: Themes = 'Orange';
const defaultColorMode: ColorMode = 'system';
const ThemesContext = createNamedContext<{
  theme: Themes;
  colorMode: ColorMode;
  resolvedMode: Exclude<ColorMode, 'system'>;
  onChange: (theme: Themes) => void;
  onColorModeChange: (colorMode: ColorMode) => void;
}>('fett-theme', {
  theme: defaultTheme,
  colorMode: defaultColorMode,
  resolvedMode: 'light',
  onChange: () => {},
  onColorModeChange: () => {},
});

export const useThemeContext = () => useContext(ThemesContext);

const commonColors = {
  errorColor: '#FF433D',
  successColor: '#00AA5A',
  infoColor: '#5F8EFC',
};

const mljColors = {
  primaryColor: '#fa6400',
  ...commonColors,
};
export const themesColors = {
  Blue: {
    primaryColor: '#3366FF',
    ...commonColors,
  },
  Orange: {
    primaryColor: '#ee4e2d',
    ...commonColors,
  },
  Purple: {
    primaryColor: '#6954F0',
    ...commonColors,
  },
};
const themesVars: Record<Themes, any> = {
  Blue: css`
    --brand-primary: ${themesColors.Blue.primaryColor};
    --brand-primary-hover: #6690ff;
    --brand-primary-active: #254edb;
    --brand-primary-soft: #cbdbfd;
    --fett-colors-primary: ${themesColors.Blue.primaryColor}; // app 主色，跟antd组件保持一致
    --fett-colors-primary-focus: #254edb;
    --fett-colors-primary-hover: #6690ff;
    --fett-colors-primary-light: #cbdbfd;
    --fett-colors-second: #fff7f7; // 套餐倒计时弹窗背景色
    --fett-colors-price: #f1694f; // 价格字体色
  `,
  Orange: css`
    --brand-primary: ${themesColors.Orange.primaryColor};
    --brand-primary-hover: #f4845f;
    --brand-primary-active: #cc3020;
    --brand-primary-soft: #fed8c8;
    --fett-colors-primary: ${themesColors.Orange.primaryColor}; // app 主色，跟antd组件保持一致
    --fett-colors-primary-focus: #cc3020;
    --fett-colors-primary-hover: #f4845f;
    --fett-colors-primary-light: #fed8c8;
    --fett-colors-second: #fff7f7; // 套餐倒计时弹窗背景色
    --fett-colors-price: #f1694f; // 价格字体色
  `,
  Purple: css`
    --brand-primary: ${themesColors.Purple.primaryColor};
    --brand-primary-hover: #9581fc;
    --brand-primary-active: #3129a3;
    --brand-primary-soft: #f4f0ff;
    --fett-colors-primary: ${themesColors.Purple.primaryColor}; // app 主色，跟antd组件保持一致
    --fett-colors-primary-focus: #3129a3;
    --fett-colors-primary-hover: #9581fc;
    --fett-colors-primary-light: #f4f0ff;
    --fett-colors-second: #fff7f7; // 套餐倒计时弹窗背景色
    --fett-colors-price: #f1694f; // 价格字体色
  `,
};
const commonVars = css`
  --fett-app-primary-color: var(--ant-primary-color);
  --fett-app-sider-nav-primary-bg: var(--fett-sider-nav-bg);
  --fett-free-trail-banner-primay-color: var(--fett-colors-primary);
  --fett-free-trail-banner-bg-color: var(--fett-colors-second);
`;
const diyVars = css`
  --fett-right-height-modal-scroll-body-margin: 4px;
`;
const lightModeVars = css`
  color-scheme: light;
  --fett-bg-app: #f0f2f5;
  --fett-sider-nav-bg: var(--fett-app-primary-color);
  --fett-sider-item-text: rgba(255, 255, 255, 0.5);
  --fett-sider-item-hover-bg: #ffffff;
  --fett-sider-item-active-bg: #ffffff;
  --fett-bg-sider: #ffffff;
  --fett-bg-panel: #ffffff;
  --fett-bg-elevated: #ffffff;
  --fett-bg-subtle: #f7f9fc;
  --fett-bg-hover: #eef3fb;
  --fett-bg-active: #e9f3ff;
  --fett-bg-input: #ffffff;
  --fett-bg-mask: rgba(17, 24, 39, 0.45);
  --fett-border-default: #f0f2f5;
  --fett-border-strong: #d3d7e0;
  --fett-text-primary: #232426;
  --fett-text-secondary: #515359;
  --fett-text-tertiary: #84868c;
  --fett-text-quaternary: #b5b9bf;
  --fett-fill-600: #464d5a;
  --fett-fill-500: #81868f;
  --fett-fill-400: #cbcdd1;
  --fett-fill-300: #e9ebed;
  --fett-fill-200: #f3f5f7;
  --fett-fill-100: #f9fafb;
  --fett-fill-0: #ffffff;
  --fett-streak-white: #f0f2f5;
  --fett-streak-grey: #e1e4eb;
  --fett-content-400: #1d2129;
  --fett-content-300: #4e5969;
  --fett-content-200: #86909c;
  --fett-content-100: #c9cdd4;
  --fett-bg-light: #e9f3ff;
  --fett-chat-bubble-customer: #ffffff;
  --fett-chat-bubble-agent: #d6e8ff;
  --fett-chat-bubble-system: #f5f6f8;
  --fett-chat-card-bg: #ffffff;
  --fett-chat-card-border: #d9e5fd;
  --fett-chat-editor-bg: #ffffff;
  --fett-product-panel-bg: #ffffff;
  --fett-product-card-bg: #ffffff;
  --fett-product-card-hover: #f7f9fc;
  --fett-product-card-border: #f0f2f5;
  --fett-product-table-stripe: #f7f9fa;
  --fett-surface-warning-bg: #fffaf0;
  --fett-surface-warning-border: #faedd2;
  --fett-surface-warning-text: #f2a100;
  --fett-surface-error-bg: #fff7f7;
  --fett-surface-error-border: #ffd9d9;
  --fett-surface-error-text: #f05b5b;
  --fett-surface-success-bg: #ebfaf3;
  --fett-surface-success-border: #caefdb;
  --fett-surface-success-text: #00aa5a;
  --fett-surface-info-bg: #e6f7ff;
  --fett-surface-info-border: #c8e7fb;
  --fett-surface-info-text: #096dd9;
  --fett-scrollbar-thumb: hsla(0, 0%, 70.6%, 0.5);
  --fett-chat-dialogue-bg: #f2f5f9;
  --fett-chat-dialogue-glow: rgba(91, 132, 255, 0.08);
  --fett-conv-item-hover: #f7f9fc;
  --fett-conv-item-active: #ececed;
  --fett-conv-item-affiliate-active: #e7e9ee;
  --fett-conv-item-remind: #ffe492;
  --fett-conv-item-remind-active: #fcd45c;
  --fett-conv-item-unprocessed: #fff4eb;
  --fett-conv-item-unprocessed-active: #ffe2c8;
  --theme-background-color: #e9f3ff;
  --theme-remind-color: #ffab00;
  --theme-fill-1: var(--fett-fill-600);
  --theme-fill-2: var(--fett-fill-500);
  --theme-fill-3: var(--fett-fill-400);
  --theme-fill-4: var(--fett-fill-300);
  --theme-fill-5: var(--fett-fill-200);
  --theme-fill-6: var(--fett-fill-100);
  --theme-fill-7: var(--fett-fill-0);
  --theme-text-1: var(--fett-content-400);
  --theme-text-2: var(--fett-content-300);
  --theme-text-3: var(--fett-content-200);
  --theme-text-4: var(--fett-content-200);
  --theme-border-1: var(--fett-streak-white);
  --theme-border-2: var(--fett-streak-grey);
`;
const darkModeVars = css`
  color-scheme: dark;
  --fett-bg-app: #11161d;
  --fett-sider-nav-bg: #131923;
  --fett-sider-item-text: rgba(201, 213, 228, 0.72);
  --fett-sider-item-hover-bg: rgba(112, 147, 255, 0.12);
  --fett-sider-item-active-bg: rgba(112, 147, 255, 0.18);
  --fett-bg-sider: #161d27;
  --fett-bg-panel: #18202a;
  --fett-bg-elevated: #202a36;
  --fett-bg-subtle: #141b24;
  --fett-bg-hover: #273242;
  --fett-bg-active: #223043;
  --fett-bg-input: #1b2430;
  --fett-bg-mask: rgba(3, 6, 10, 0.72);
  --fett-border-default: #2c3747;
  --fett-border-strong: #3a4759;
  --fett-text-primary: #eef4fb;
  --fett-text-secondary: #cad5e4;
  --fett-text-tertiary: #91a0b4;
  --fett-text-quaternary: #6f8096;
  --fett-fill-600: #eef4fb;
  --fett-fill-500: #cad5e4;
  --fett-fill-400: #91a0b4;
  --fett-fill-300: #3a4759;
  --fett-fill-200: #25303d;
  --fett-fill-100: #18202a;
  --fett-fill-0: #202a36;
  --fett-streak-white: #2c3747;
  --fett-streak-grey: #3a4759;
  --fett-content-400: #eef4fb;
  --fett-content-300: #cad5e4;
  --fett-content-200: #91a0b4;
  --fett-content-100: #6f8096;
  --fett-bg-light: rgba(112, 147, 255, 0.16);
  --fett-chat-dialogue-bg: #131a23;
  --fett-chat-dialogue-glow: rgba(112, 147, 255, 0.1);
  --fett-chat-bubble-customer: #25303d;
  --fett-chat-bubble-agent: #1c314d;
  --fett-chat-bubble-system: #1c2531;
  --fett-chat-card-bg: #202a36;
  --fett-chat-card-border: #3a4759;
  --fett-chat-editor-bg: #1b2430;
  --fett-product-panel-bg: #18202a;
  --fett-product-card-bg: #202a36;
  --fett-product-card-hover: #273242;
  --fett-product-card-border: #3a4759;
  --fett-product-table-stripe: #223043;
  --fett-surface-warning-bg: rgba(255, 176, 32, 0.12);
  --fett-surface-warning-border: rgba(255, 176, 32, 0.3);
  --fett-surface-warning-text: #ffb020;
  --fett-surface-error-bg: rgba(255, 107, 107, 0.14);
  --fett-surface-error-border: rgba(255, 107, 107, 0.32);
  --fett-surface-error-text: #ff8a8a;
  --fett-surface-success-bg: rgba(0, 170, 90, 0.16);
  --fett-surface-success-border: rgba(0, 170, 90, 0.34);
  --fett-surface-success-text: #63d79a;
  --fett-surface-info-bg: rgba(95, 142, 252, 0.16);
  --fett-surface-info-border: rgba(95, 142, 252, 0.34);
  --fett-surface-info-text: #8cb0ff;
  --fett-scrollbar-thumb: rgba(148, 163, 184, 0.42);
  --fett-conv-item-hover: rgba(112, 147, 255, 0.08);
  --fett-conv-item-active: rgba(112, 147, 255, 0.16);
  --fett-conv-item-affiliate-active: rgba(155, 171, 196, 0.16);
  --fett-conv-item-remind: rgba(255, 192, 90, 0.16);
  --fett-conv-item-remind-active: rgba(255, 192, 90, 0.24);
  --fett-conv-item-unprocessed: rgba(244, 140, 78, 0.14);
  --fett-conv-item-unprocessed-active: rgba(244, 140, 78, 0.22);
  --theme-background-color: rgba(91, 140, 255, 0.16);
  --theme-remind-color: #ffb020;
  --theme-fill-1: var(--fett-fill-600);
  --theme-fill-2: var(--fett-fill-500);
  --theme-fill-3: var(--fett-fill-400);
  --theme-fill-4: var(--fett-fill-300);
  --theme-fill-5: var(--fett-fill-200);
  --theme-fill-6: var(--fett-fill-100);
  --theme-fill-7: var(--fett-fill-0);
  --theme-text-1: var(--fett-content-400);
  --theme-text-2: var(--fett-content-300);
  --theme-text-3: var(--fett-content-200);
  --theme-text-4: var(--fett-content-100);
  --theme-border-1: var(--fett-streak-white);
  --theme-border-2: var(--fett-streak-grey);
`;
export const ThemesProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    systemSetting: { theme: settingTheme, colorMode },
    handleThemeSettingChange,
    handleColorModeSettingChange,
  } = useSystem();
  const { whiteListConfig: { AE_CONVERSATION = false } = {} } = useOrgConfigStateValue();
  const theme = isQuickChat || AE_CONVERSATION ? 'Purple' : settingTheme;
  const [systemResolvedMode, setSystemResolvedMode] = useState<'light' | 'dark'>('light');
  const resolvedMode = colorMode === 'system' ? systemResolvedMode : colorMode;
  const updateTheme = useCallback(
    (theme: Themes) => {
      !AE_CONVERSATION && handleThemeSettingChange(theme);
    },
    [handleThemeSettingChange, AE_CONVERSATION],
  );
  const updateColorMode = useCallback(
    (nextColorMode: ColorMode) => {
      handleColorModeSettingChange(nextColorMode);
    },
    [handleColorModeSettingChange],
  );
  const CssVarRoot = useMemo(() => {
    const themeVars = themesVars[theme];
    const modeVars = resolvedMode === 'dark' ? darkModeVars : lightModeVars;
    return isMlj
      ? css`
          :root {
            ${themeVars}
            ${modeVars}
            ${commonVars}
            ${diyVars}
            --fett-app-primary-color: ${mljColors.primaryColor}; // 马六甲专用
          }
        `
      : css`
          :root {
            ${themeVars}
            ${modeVars}
            ${commonVars}
            ${diyVars}
            --fett-app-primary-color: var(--fett-colors-primary);
          }
        `;
  }, [resolvedMode, theme]);
  useMount(() => {
    updateTheme(theme);
  });
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const syncSystemMode = () => {
      setSystemResolvedMode(media.matches ? 'dark' : 'light');
    };
    syncSystemMode();
    if (media.addEventListener) {
      media.addEventListener('change', syncSystemMode);
      return () => {
        media.removeEventListener('change', syncSystemMode);
      };
    }
    media.addListener(syncSystemMode);
    return () => {
      media.removeListener(syncSystemMode);
    };
  }, []);
  useEffect(() => {
    ConfigProvider.config({
      theme: isMlj ? mljColors : themesColors[theme],
    });
  }, [theme]);
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.brand = theme.toLowerCase();
    root.dataset.colorMode = resolvedMode;
    root.dataset.colorModePreference = colorMode;
  }, [colorMode, resolvedMode, theme]);
  return (
    <ThemesContext.Provider
      value={{
        theme,
        colorMode,
        resolvedMode,
        onChange: updateTheme,
        onColorModeChange: updateColorMode,
      }}
    >
      <>
        <Global styles={CssVarRoot} />
        {children}
      </>
    </ThemesContext.Provider>
  );
};
export default ThemesProvider;
